<template>
	<BaseCmsPage v-slot="{page}">
		<ClientOnly>
			<div class="main">
				<div class="wrapper">
					<BaseWebshopCart v-slot="{parcels, counter, urls, onFinishShopping}">
						<div class="wc-col wc-col1">
							<h1 class="w-title">{{ page.seo_h1 }} <span class="w-counter" v-if="counter">{{counter}}</span></h1>

							<BaseWebshopCartErrors v-slot="{errorsItems, warningsItems}">
								<template v-if="errorsItems?.length">
									<div class="global-error cart-global-warning" v-for="error in errorsItems" :key="error"><BaseCmsLabel :code="error.label_name" /></div>
								</template>
								<template v-if="warningsItems?.length">
									<div class="global-warning cart-global-warning" v-for="warning in warningsItems" :key="warning"><BaseCmsLabel :code="warning.label_name" /></div>
								</template>
							</BaseWebshopCartErrors>

							<div class="w-cart-items" v-if="parcels[0]?.items?.length">
								<WebshopCartItem v-for="item in parcels[0].items" :data="item" :key="item.shopping_cart_code" />
							</div>

							<div class="w-empty-cart" v-else>
								<BaseCmsLabel code="empty_shopping_cart" v-interpolation tag="div" />
							</div>
						</div>
						<div class="wc-col wc-col2" v-if="parcels[0]?.items?.length">
							<div class="wc-col2-top">
								<WebshopCouponForm mode="webshop" />
								
								<div ref="shoppingCartTotal" class="w-col2-cnt">
									<BaseCmsLabel code="total_title" tag="div" class="total-title" />
									<WebshopTotal />
									
									<BaseWebshopCartErrors v-slot="{errors, warnings}">
										<NuxtLink @click="onFinishShopping" v-if="urls.webshop_customer && !errors && !warnings" :to="urls.webshop_customer" class="btn btn-orange btn-finish-shopping"><BaseCmsLabel code="finish_shopping" tag="span" /></NuxtLink>
									</BaseWebshopCartErrors>
								</div>
							</div>

							<BaseCmsRotator :fetch="{code: 'shopping_cart_benefits', limit: 5}" v-slot="{items: benefits}"> 
								<div class="shopping-cart-benefits" v-if="benefits?.length">
									<div class="sc-benefit" v-for="benefit in benefits" :key="benefit.id">
										<div class="sc-benefit-image"><BaseUiImage :data="benefit.image_thumbs?.['width200-height200-crop1']" /></div>
										<div class="sc-benefit-cnt">
											<div class="sc-benefit-title">{{benefit.title}}</div>
											<div class="sc-benefit-content" v-html="benefit.content" v-interpolation />
										</div>
									</div>
								</div>
							</BaseCmsRotator> 
						</div>
					</BaseWebshopCart>
				</div>
			</div>
			<template #fallback>
				<BaseThemeUiLoading />
			</template>
		</ClientOnly>
	</BaseCmsPage>
</template>

<script setup>
	const labels = useLabels();
	const {formatCurrency} = useCurrency();
	const {mobileBreakpoint} = inject('rwd');
</script>


<style lang="less" scoped>
	.main{
		border-top: 1px solid var(--gray); padding: 60px 0 100px;
		@media (max-width: @l){padding: 40px 0 60px;}
		@media (max-width: @m){padding: 30px 0 32px;}	
	}
	.wrapper{
		display: flex; gap: 80px;
		@media (max-width: @l){gap: 30px;}
		@media (max-width: @m){flex-flow: column; gap: 0; padding: 0;}	
	}
	.wc-col1{flex-grow: 1; flex-shrink: 1;}
	.wc-col2{
		width: 33.2%; max-width: 460px; flex-grow: 0; flex-shrink: 0;
		@media (max-width: @l){width: 38%; max-width: 400px;}
		@media (max-width: @m){width: 100%; max-width: 100%;}
	}
	.w-title{
		position: relative; display: flex; align-items: center; padding-left: 52px; font-size: 40px; line-height: 1.15; padding-bottom: 0; margin-bottom: 24px;
		&:before{.pseudo(32px,32px); .icon-cart(); font: 32px/1 var(--fonti); color: var(--blue); left: 0;}
		@media (max-width: @l){font-size: 32px;}
		@media (max-width: @m){
			margin: 0 16px 20px; width: calc(~"100% - 32px"); font-size: 24px; padding-left: 38px;
			&:before{width: 26px; height: 26px; font-size: 26px; top: -1px;}	
		}
	}
	.w-counter{
		position: absolute; display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; background: var(--orange); border-radius: 50%; top: 0; left: 20px; color: #fff; font-size: 12px; line-height: 1; font-weight: 700;
		@media (max-width: @m){width: 20px; height: 20px; font-size: 11px; top: -9px; left: auto; left: 13px;}	
	}
	.w-cart-items{position: relative; display: flex; flex-flow: column; width: 100%;}
	.wc-col2-top{
		position: relative; display: flex; flex-flow: column; width: 100%; background: #fff; border-radius: 8px; box-shadow: 0 10px 40px rgba(0,0,0,0.1); padding: 42px 48px 40px;
		@media (max-width: @l){padding: 32px;}
		@media (max-width: @m){box-shadow: none; border-radius: 0; padding: 26px 16px 0;}
	}
	.total-title{
		display: block; font-size: 20px; line-height: 1.4; margin-bottom: 10px;
		@media (max-width: @m){font-size: 18px; margin-bottom: 8px;}	
	}
	:deep(.w-totals){
		font-size: 14px;
		@media (max-width: @m){font-size: 13px;}	
	}
	:deep(.w-totals-cart-total){font-size: 16px;}
	.btn-finish-shopping{
		width: 100%; margin-top: 24px;
		@media (max-width: @m){margin-top: 20px;}	
	}
	.w-empty-cart{
		@media (max-width: @m){padding: 0 16px;}
	}

	.shopping-cart-benefits{
		position: relative; display: flex; flex-flow: column; gap: 20px; margin-top: 40px; padding: 0 48px;
		@media (max-width: @l){margin-top: 30px; padding: 0 32px;}	
		@media (max-width: @t){margin-top: 24px; padding: 0;}
		@media (max-width: @m){padding: 0 16px;}	
	}
	.sc-benefit{
		position: relative; display: flex; align-items: center; gap: 16px;
		@media (max-width: @t){gap: 12px;}	
		@media (max-width: @m){gap: 14px;}
	}
	.sc-benefit-image{
		position: relative; display: flex; align-items: center; justify-content: center; width: 72px; height: 72px; background: var(--lightGray); border-radius: 50%; flex-grow: 0; flex-shrink: 0;
		:img{display: block; width: auto; height: auto; max-width: 35px; max-height: 30px;}
		@media (max-width: @t){width: 60px; height: 60px;}
	}
	.sc-benefit-title{
		display: block; font-size: 14px; line-height: 1.6; margin-bottom: 2px; font-weight: 700;
		@media (max-width: @m){margin-bottom: 0;}
	}
	.sc-benefit-content{
		display: block; font-size: 12px; line-height: 1.6;
		@media (max-width: @l){font-size: 11px;}
		@media (max-width: @m){font-size: 12px;}	
	}
</style>